<script setup lang="ts">
import {
  type GoogleDriveIntegrationStatus,
  type GaiaTokenCreate,
  type GaiaTokenInDb,
  IntegrationState,
  type SlackIntegrationStatus,
  type YoutrustIntegrationInDb,
  type YoutrustIntegrationStatus,
} from '@/api';
import TokenForm from '@/components/TokenForm.vue';
import GoogleDriveVerificationNoticeModal from '@/components/GoogleDriveVerificationNoticeModal.vue';
import WordPluginInstallationGuideModal from '@/components/WordPluginInstallationGuideModal.vue';
import { useGaiaStore } from '@/stores/gaiaStore';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organizationStore';
import {
  completeGoogleDriveOAuth,
  disconnectGoogleDriveIntegration,
  fetchGoogleDriveIntegrationStatus,
  requestGoogleDriveAuthorizationUrl,
} from '@/services/integration/googleDriveIntegrationService';
import {
  INTEGRATION_OAUTH_SCOPE_KEY,
  setIntegrationOauthContext,
} from '@/services/integration/integrationOauthService';
import {
  createYoutrustSigningIntegration,
  fetchYoutrustSigningIntegrationStatus,
  removeYoutrustSigningIntegration,
} from '@/services/integration/youtrustIntegrationService';
import {
  fetchSlackChannels,
  fetchSlackIntegrationStatus,
  hasAllSlackScopes,
  removeSlackIntegration,
  requestSlackAuthorizationUrl,
  sendSlackChannelTestMessage,
} from '@/services/integration/slackIntegrationService';
import { extractErrorDetail } from '@/utils/errorHandler';
import { extractTokenErrorMessage } from '@/utils/tokenErrorHandler';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { logger } from '@/utils/logger';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaInputGroupLabel from '@/components/design/base/inputs/GaiaInputGroupLabel.vue';
import GaiaInputGroup from '@/components/design/base/inputs/GaiaInputGroup.vue';
import GaiaPasswordInput from '@/components/design/base/inputs/GaiaPasswordInput.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import GaiaRemoteSelect, {
  type GaiaRemotePage,
} from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaFieldRow from '@/components/design/base/GaiaFieldRow.vue';
import GaiaStatusChip, { type GaiaChipTone } from '@/components/design/base/GaiaStatusChip.vue';
import GaiaPopconfirm from '@/components/design/composite/GaiaPopconfirm.vue';
import GaiaIntegrationCard from '@/components/design/domain/integrations/GaiaIntegrationCard.vue';
import GaiaJiraCard from '@/components/design/domain/integrations/GaiaJiraCard.vue';
const message = useGaiaToast();
const googleDriveNoticeModal = ref<InstanceType<typeof GoogleDriveVerificationNoticeModal>>();
const wordPluginGuideModal = ref<InstanceType<typeof WordPluginInstallationGuideModal>>();
const jiraCard = ref<InstanceType<typeof GaiaJiraCard>>();

const wordPluginManifestFile = computed(() => {
  const env = import.meta.env.VITE_ENV;
  const mode = env === 'prod' ? 'production' : env;
  return `manifest.${mode}.xml`;
});

async function handleDownloadWordPluginManifest(): Promise<void> {
  try {
    const resp = await fetch(`/manifests/${wordPluginManifestFile.value}`);
    if (!resp.ok) {
      throw new Error(`Manifest not available (${resp.status})`);
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = wordPluginManifestFile.value;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  } catch {
    message.error('Failed to download Word add-in manifest.');
  }
}
const gaiaStore = useGaiaStore();
const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();

const youtrustApiKeyFormValid = computed(() => {
  if (youtrustIntegrationSandboxMode.value) {
    return (
      Boolean(youtrustManagementApiKey.value.trim()) && Boolean(youtrustSigningApiKey.value.trim())
    );
  }
  return Boolean(youtrustSigningApiKey.value.trim());
});

const currentToken = ref<GaiaTokenInDb | null>(null);
const googleDriveStatus = ref<GoogleDriveIntegrationStatus | null>(null);
const youtrustIntegration = ref<YoutrustIntegrationInDb | null>(null);
const youtrustStatus = ref<YoutrustIntegrationStatus | null>(null);
const slackStatus = ref<SlackIntegrationStatus | null>(null);

const loading = ref(false);
const googleLoading = ref(false);
const youtrustLoading = ref(false);
const slackLoading = ref(false);
const googleActionLoading = ref(false);
const youtrustActionLoading = ref(false);
const slackActionLoading = ref(false);
const showCreateModal = ref(false);
const showEditModal = ref(false);
const showYoutrustModal = ref(false);
const youtrustManagementApiKey = ref('');
const youtrustSigningApiKey = ref('');
const youtrustIntegrationSandboxMode = ref<boolean>(false);
const slackTestChannelId = ref<string | null>(null);
const slackTestSending = ref(false);
const showSlackTestModal = ref(false);

const tokenFormRef = ref<InstanceType<typeof TokenForm>>();

const googleState = computed<IntegrationState>(() => {
  return googleDriveStatus.value?.state ?? IntegrationState.DISCONNECTED;
});

const isGoogleConnected = computed(() => googleState.value === IntegrationState.CONNECTED);

const googleStateTone = computed<GaiaChipTone>(() => {
  if (isGoogleConnected.value) return 'success';
  return 'neutral';
});

const googleStateLabel = computed(() => {
  if (isGoogleConnected.value) return 'Connected';
  return 'Not Connected';
});

const googleHelperMessage = computed(() => {
  if (isGoogleConnected.value) {
    return 'Google Drive is connected.';
  }
  return 'Connect Google Drive for organization-level integration.';
});

const youtrustState = computed<IntegrationState>(() => {
  return youtrustStatus.value?.state ?? IntegrationState.DISCONNECTED;
});

const isYoutrustConnected = computed(() => youtrustState.value === IntegrationState.CONNECTED);

const youtrustStateTone = computed<GaiaChipTone>(() => {
  if (isYoutrustConnected.value) return 'success';
  return 'neutral';
});

const youtrustStateLabel = computed(() => {
  if (isYoutrustConnected.value) return 'Connected';
  return 'Not Connected';
});

const youtrustWebhooksEnabled = computed(() => {
  return (
    youtrustIntegration.value?.has_active_webhooks ??
    youtrustStatus.value?.has_active_webhooks ??
    false
  );
});

const youtrustWebhooksLabel = computed(() => {
  return youtrustWebhooksEnabled.value ? 'Enabled' : 'Disabled';
});

const youtrustWebhooksTone = computed<GaiaChipTone>(() => {
  return youtrustWebhooksEnabled.value ? 'success' : 'neutral';
});

const youtrustHelperMessage = computed(() => {
  if (isYoutrustConnected.value) {
    return 'Youtrust is connected.';
  }
  return 'Connect Youtrust for organization-level signing integration.';
});

const slackState = computed<IntegrationState>(() => {
  return slackStatus.value?.state ?? IntegrationState.DISCONNECTED;
});

const isSlackConnected = computed(() => slackState.value === IntegrationState.CONNECTED);

// Connected, but authorized before one of the agent scopes existed: everything else keeps
// working, so this is a warning rather than a disconnection.
const slackNeedsReconnect = computed(
  () => isSlackConnected.value && !hasAllSlackScopes(slackStatus.value)
);

const slackStateTone = computed<GaiaChipTone>(() => {
  if (slackNeedsReconnect.value) return 'warning';
  if (isSlackConnected.value) return 'success';
  return 'neutral';
});

const slackStateLabel = computed(() => {
  if (slackNeedsReconnect.value) return 'Reconnect needed';
  if (isSlackConnected.value) return 'Connected';
  return 'Not Connected';
});

const slackHelperMessage = computed(() => {
  if (slackNeedsReconnect.value) {
    return 'Slack is connected, but this workspace is missing permissions Gaia now needs. Reconnect to grant them — intake agents and playbook Slack actions will not work until you do.';
  }
  if (isSlackConnected.value) {
    return 'Slack is connected.';
  }
  return 'Connect Slack: You will be redirected to Slack to approve the Gaia app.';
});

function getQueryValue(key: string): string {
  const queryValue = route.query[key];
  if (Array.isArray(queryValue)) {
    return queryValue[0] ?? '';
  }
  return queryValue ?? '';
}

// The OAuth callback view forwards the Jira integration state through the query string, so it
// arrives here as a plain string rather than as the enum member.
const JIRA_PENDING_STATUS: string = IntegrationState.PENDING;

function isTruthyQueryValue(value: string): boolean {
  const normalized = value.toLowerCase();
  return ['1', 'true', 'ok', 'success', 'connected'].includes(normalized);
}

async function handleOauthReturnFeedback(): Promise<void> {
  const integration = getQueryValue('integration').toLowerCase();
  const status = getQueryValue('status');
  const result = getQueryValue('result');
  const googleFlag = getQueryValue('google_drive');
  const code = getQueryValue('code');
  const oauthError = getQueryValue('error');
  const queryScope = getQueryValue('scope').toLowerCase();
  const storedScope = window.sessionStorage.getItem(INTEGRATION_OAUTH_SCOPE_KEY);
  const oauthScope =
    queryScope === 'user' || queryScope === 'organisation'
      ? queryScope
      : storedScope === 'user' || storedScope === 'organisation'
        ? storedScope
        : 'organisation';
  const hasOauthMarker =
    'code' in route.query ||
    'state' in route.query ||
    'integration' in route.query ||
    'status' in route.query ||
    'result' in route.query ||
    'google_drive' in route.query ||
    'scope' in route.query ||
    'error' in route.query;

  // A bare ?code=/?error= (no integration param) is the legacy Google direct
  // redirect to this page; Slack and Jira returns always carry their integration param.
  const isSlackOauthFlow = integration === 'slack';
  const isJiraOauthFlow = integration === 'jira';
  const isGoogleOauthFlow =
    integration === 'google_drive' ||
    integration === 'google-drive' ||
    googleFlag.length > 0 ||
    (integration.length === 0 && (code.length > 0 || oauthError.length > 0));

  if (isSlackOauthFlow && oauthError) {
    message.error(
      oauthError === 'access_denied'
        ? 'Slack connection was cancelled.'
        : `Slack connection failed: ${oauthError}`
    );
  }

  if (
    isSlackOauthFlow &&
    !oauthError &&
    (isTruthyQueryValue(status) || isTruthyQueryValue(result))
  ) {
    message.success('Slack connected successfully.');
  }

  if (isJiraOauthFlow && oauthError) {
    message.error(
      oauthError === 'access_denied'
        ? 'Jira connection was cancelled.'
        : `Jira connection failed: ${oauthError}`
    );
  }

  // One Atlassian consent can cover several sites, so the callback lands on `pending` and the
  // integration only becomes usable once a site is committed — carry the admin straight there.
  if (isJiraOauthFlow && !oauthError) {
    if (status === JIRA_PENDING_STATUS) {
      message.info('Jira authorized. Choose which Jira site to use.');
      jiraCard.value?.openSiteModal();
    } else if (isTruthyQueryValue(status)) {
      message.success('Jira connected successfully.');
    }
  }

  if (isGoogleOauthFlow && oauthError) {
    message.error(`Google Drive OAuth failed: ${oauthError}`);
  }

  if (isGoogleOauthFlow && code) {
    try {
      googleDriveStatus.value = await completeGoogleDriveOAuth(code, oauthScope);
      message.success(
        oauthScope === 'user'
          ? 'Your Google Drive connected successfully.'
          : 'Google Drive connected successfully.'
      );
    } catch (error) {
      logger.error('Failed to complete Google Drive OAuth:', error);
      message.error(extractErrorDetail(error, 'Failed to complete Google Drive OAuth.'));
    }
  }

  if (
    isGoogleOauthFlow &&
    !code &&
    !oauthError &&
    (isTruthyQueryValue(status) || isTruthyQueryValue(result) || isTruthyQueryValue(googleFlag))
  ) {
    message.success('Google Drive connected successfully.');
  }

  if (hasOauthMarker) {
    const nextQuery = { ...route.query };
    delete nextQuery.code;
    delete nextQuery.state;
    delete nextQuery.integration;
    delete nextQuery.status;
    delete nextQuery.result;
    delete nextQuery.google_drive;
    delete nextQuery.scope;
    delete nextQuery.error;
    await router.replace({ query: nextQuery }).catch(() => undefined);
  }

  if (isGoogleOauthFlow && oauthScope === 'user') {
    await router.replace({ name: 'settings-profile' }).catch(() => undefined);
  }

  window.sessionStorage.removeItem(INTEGRATION_OAUTH_SCOPE_KEY);
}

onMounted(async () => {
  await Promise.all([
    fetchCurrentToken(),
    fetchGoogleDriveStatus(),
    fetchYoutrustStatus(),
    fetchSlackStatus(),
  ]);
  await handleOauthReturnFeedback();
  window.addEventListener('focus', handleWindowFocus);
});

onUnmounted(() => {
  window.removeEventListener('focus', handleWindowFocus);
});

async function fetchCurrentToken(): Promise<void> {
  loading.value = true;
  try {
    currentToken.value = await gaiaStore.fetchCurrentToken();
  } catch (error: unknown) {
    const errorMessage = extractTokenErrorMessage(error);
    message.error(errorMessage);
    logger.error(errorMessage, error);
  } finally {
    loading.value = false;
  }
}

async function fetchGoogleDriveStatus(): Promise<void> {
  googleLoading.value = true;
  try {
    googleDriveStatus.value = await fetchGoogleDriveIntegrationStatus();
  } catch (error) {
    logger.error('Failed to fetch Google Drive status:', error);
    message.error(extractErrorDetail(error, 'Failed to fetch Google Drive status.'));
    googleDriveStatus.value = null;
  } finally {
    googleLoading.value = false;
  }
}

async function fetchYoutrustStatus(): Promise<void> {
  youtrustLoading.value = true;
  try {
    youtrustStatus.value = await fetchYoutrustSigningIntegrationStatus();
  } catch (error) {
    logger.error('Failed to fetch Youtrust status:', error);
    message.error(extractErrorDetail(error, 'Failed to fetch Youtrust status.'));
    youtrustStatus.value = null;
  } finally {
    youtrustLoading.value = false;
  }
}

async function fetchSlackStatus(): Promise<void> {
  slackLoading.value = true;
  try {
    slackStatus.value = await fetchSlackIntegrationStatus();
  } catch (error) {
    logger.error('Failed to fetch Slack status:', error);
    message.error(extractErrorDetail(error, 'Failed to fetch Slack status.'));
    slackStatus.value = null;
  } finally {
    slackLoading.value = false;
  }
}

function handleWindowFocus(): void {
  void fetchGoogleDriveStatus();
  void fetchYoutrustStatus();
  void fetchSlackStatus();
  // Jira can be auto-disconnected server-side (revoked consent, dead refresh token), so its
  // status must never be cached for the session.
  void jiraCard.value?.refresh();
}

async function handleGoogleConnect(): Promise<void> {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to manage integrations.');
    return;
  }

  googleActionLoading.value = true;
  try {
    setIntegrationOauthContext({
      provider: 'google_drive',
      origin: 'integrations_org',
      scope: 'organisation',
    });

    const latestStatus = await fetchGoogleDriveIntegrationStatus('organisation');
    googleDriveStatus.value = latestStatus;
    if (latestStatus.connected) {
      message.warning(
        'Organization Google Drive is already connected. Disconnect it before connecting another account.'
      );
      return;
    }

    const authorizationUrl = await requestGoogleDriveAuthorizationUrl();
    window.location.href = authorizationUrl;
  } catch (error) {
    logger.error('Failed to start Google Drive OAuth flow:', error);
    message.error(extractErrorDetail(error, 'Could not start Google Drive connection.'));
  } finally {
    googleActionLoading.value = false;
  }
}

async function handleGoogleDisconnect(): Promise<void> {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to manage integrations.');
    return;
  }

  googleActionLoading.value = true;
  try {
    googleDriveStatus.value = await disconnectGoogleDriveIntegration();
    message.success('Google Drive disconnected successfully.');
  } catch (error) {
    logger.error('Failed to disconnect Google Drive:', error);
    message.error(extractErrorDetail(error, 'Failed to disconnect Google Drive.'));
  } finally {
    googleActionLoading.value = false;
  }
}

function resetYoutrustModal(): void {
  showYoutrustModal.value = false;
  youtrustManagementApiKey.value = '';
  youtrustSigningApiKey.value = '';
}

const organizationStore = useOrganizationStore();

/** Connecting or removing Youtrust changes which providers the org can use. */
async function refreshSigningProviders(): Promise<void> {
  await organizationStore.fetchActiveOrganization();
}

async function handleYoutrustConnect(): Promise<void> {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to manage integrations.');
    return;
  }

  const signingApiKey = youtrustSigningApiKey.value.trim();
  let managemenApiKey = youtrustManagementApiKey.value.trim();
  if (!signingApiKey) {
    message.error('Please enter a Youtrust access token.');
    return;
  }
  if (!managemenApiKey) {
    managemenApiKey = signingApiKey;
  }

  youtrustActionLoading.value = true;
  try {
    youtrustIntegration.value = await createYoutrustSigningIntegration(
      managemenApiKey,
      signingApiKey
    );
    youtrustStatus.value = {
      state: youtrustIntegration.value.state,
      connected: youtrustIntegration.value.state === IntegrationState.CONNECTED,
      has_active_webhooks: youtrustIntegration.value.has_active_webhooks,
    };
    message.success('Youtrust connected successfully.');
    resetYoutrustModal();
    void refreshSigningProviders();
  } catch (error) {
    logger.error('Failed to create Youtrust integration:', error);
    message.error(extractErrorDetail(error, 'Failed to connect Youtrust.'));
  } finally {
    youtrustActionLoading.value = false;
  }
}

async function handleYoutrustRemove(): Promise<void> {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to manage integrations.');
    return;
  }

  youtrustActionLoading.value = true;
  try {
    await removeYoutrustSigningIntegration();
    youtrustIntegration.value = null;
    youtrustStatus.value = await fetchYoutrustSigningIntegrationStatus();
    message.success('Youtrust integration removed successfully.');
    void refreshSigningProviders();
  } catch (error) {
    logger.error('Failed to remove Youtrust integration:', error);
    message.error(extractErrorDetail(error, 'Failed to remove Youtrust integration.'));
  } finally {
    youtrustActionLoading.value = false;
  }
}

// Channels are searched server-side; the field owns the debounce, the options,
// the loading flag and the chosen channel's label (`keepSelected`), which this
// file used to keep by hand so the select never showed a bare channel id.
async function fetchSlackChannelOptions(text: string): Promise<GaiaRemotePage> {
  const channels = await fetchSlackChannels(text);
  return {
    options: channels.map(channel => ({
      label: `#${channel.name}`,
      value: channel.id,
    })),
  };
}

function onSlackChannelError(error: unknown): void {
  logger.error('Failed to load Slack channels:', error);
  message.error(extractErrorDetail(error, 'Failed to load Slack channels.'));
}

function openSlackTestModal(): void {
  slackTestChannelId.value = null;
  showSlackTestModal.value = true;
}

async function handleSlackSendTestMessage(): Promise<void> {
  const channelId = slackTestChannelId.value;
  if (!channelId) {
    message.error('Please select a channel first.');
    return;
  }

  slackTestSending.value = true;
  try {
    await sendSlackChannelTestMessage(channelId);
    showSlackTestModal.value = false;
    message.success('Test message sent — check the channel in Slack.');
  } catch (error) {
    logger.error('Failed to send Slack test message:', error);
    message.error(extractErrorDetail(error, 'Failed to send Slack test message.'));
  } finally {
    slackTestSending.value = false;
  }
}

async function handleSlackConnect(): Promise<void> {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to manage integrations.');
    return;
  }

  slackActionLoading.value = true;
  try {
    setIntegrationOauthContext({
      provider: 'slack',
      origin: 'integrations_org',
      scope: 'organisation',
    });
    const authorizationUrl = await requestSlackAuthorizationUrl();
    window.location.href = authorizationUrl;
  } catch (error) {
    logger.error('Failed to start Slack OAuth flow:', error);
    message.error(extractErrorDetail(error, 'Could not start Slack connection.'));
  } finally {
    slackActionLoading.value = false;
  }
}

async function handleSlackDisconnect(): Promise<void> {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to manage integrations.');
    return;
  }

  slackActionLoading.value = true;
  try {
    slackStatus.value = await removeSlackIntegration();
    // The test modal is unreachable while disconnected, but its channel would
    // otherwise still be sitting there after a reconnect to another workspace.
    slackTestChannelId.value = null;
    message.success('Slack integration removed.');
  } catch (error) {
    logger.error('Failed to remove Slack integration:', error);
    message.error(extractErrorDetail(error, 'Failed to remove Slack integration.'));
  } finally {
    slackActionLoading.value = false;
  }
}

async function handleCreateSubmit(data: { name: string; token: string }): Promise<void> {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to create tokens.');
    return;
  }
  loading.value = true;
  try {
    const createData: GaiaTokenCreate = {
      name: data.name,
      token: data.token,
    };

    const newToken = await gaiaStore.createToken(createData);
    currentToken.value = newToken;
    message.success('GAIA token created successfully');

    showCreateModal.value = false;
  } catch (error: unknown) {
    const errorMessage = extractTokenErrorMessage(error);
    message.error(errorMessage);
    logger.error(errorMessage, error);
  } finally {
    loading.value = false;
  }
}

async function handleEditSubmit(data: { name: string; token: string }): Promise<void> {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to update tokens.');
    return;
  }
  loading.value = true;
  try {
    const updateData = {
      name: data.name,
      token: data.token,
    };

    const updatedToken = await gaiaStore.updateToken(updateData);
    currentToken.value = updatedToken;
    message.success('GAIA token updated successfully');

    showEditModal.value = false;
  } catch (error: unknown) {
    const errorMessage = extractTokenErrorMessage(error);
    message.error(errorMessage);
    logger.error(errorMessage, error);
  } finally {
    loading.value = false;
  }
}

async function deleteToken(): Promise<void> {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to delete tokens.');
    return;
  }
  loading.value = true;
  try {
    await gaiaStore.deleteToken();
    currentToken.value = null;
    message.success('GAIA token deleted successfully');
  } catch (error: unknown) {
    const errorMessage = extractTokenErrorMessage(error);
    message.error(errorMessage);
    logger.error(errorMessage, error);
  } finally {
    loading.value = false;
  }
}

function openEditModal(): void {
  if (!authStore.userPermissions.is_org_admin) return;
  showEditModal.value = true;
}
</script>

<template>
  <Layout>
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.organisation }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.integrations }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="16" :wrap="false">
      <n-flex vertical :size="4" :wrap="false">
        <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
          {{ navLabels.integrations }}
        </gaia-header>
        <gaia-text size="body" tone="tertiary">
          Manage external provider connections for your organization.
        </gaia-text>
      </n-flex>

      <gaia-spinner
        :show="
          loading ||
          gaiaStore.tokenLoading ||
          googleLoading ||
          youtrustLoading ||
          slackLoading ||
          Boolean(jiraCard?.loading)
        "
      >
        <div class="integrations-grid">
          <gaia-integration-card
            title="Google Drive"
            :tone="googleStateTone"
            :status-label="googleStateLabel"
            :message="googleHelperMessage"
          >
            <template v-if="googleDriveStatus?.authorized_at" #meta>
              <gaia-text size="micro" tone="muted">
                Authorized at: {{ new Date(googleDriveStatus.authorized_at).toLocaleString() }}
              </gaia-text>
            </template>

            <template #actions>
              <gaia-button
                v-if="!isGoogleConnected"
                type="primary"
                size="small"
                :loading="googleActionLoading"
                :disabled="!authStore.userPermissions.is_org_admin"
                @click="googleDriveNoticeModal?.show()"
              >
                Connect
              </gaia-button>
              <gaia-popconfirm
                v-if="isGoogleConnected"
                title="Disconnect Google Drive?"
                confirm-label="Disconnect"
                :width="320"
                @confirm="handleGoogleDisconnect"
              >
                <template #trigger>
                  <gaia-button
                    size="small"
                    type="error"
                    tertiary
                    :loading="googleActionLoading"
                    :disabled="!authStore.userPermissions.is_org_admin"
                  >
                    Disconnect
                  </gaia-button>
                </template>
                Imports already in the vault stay; new ones cannot be started until Google Drive is
                connected again.
              </gaia-popconfirm>
            </template>
          </gaia-integration-card>

          <gaia-integration-card
            title="Youtrust"
            :tone="youtrustStateTone"
            :status-label="youtrustStateLabel"
            :message="youtrustHelperMessage"
          >
            <!-- Youtrust is the one provider with a second state worth reading: the
                 signing keys can be in place while its webhooks are not. -->
            <n-flex align="center" :size="8" :wrap="false">
              <gaia-text variant="label" size="micro" tone="faint">Webhooks</gaia-text>
              <gaia-status-chip :tone="youtrustWebhooksTone" :style="{ marginLeft: 'auto' }">
                {{ youtrustWebhooksLabel }}
              </gaia-status-chip>
            </n-flex>

            <template v-if="youtrustIntegration?.created_at" #meta>
              <gaia-text size="micro" tone="muted">
                Connected at: {{ new Date(youtrustIntegration.created_at).toLocaleString() }}
              </gaia-text>
            </template>

            <template #actions>
              <gaia-button
                v-if="!isYoutrustConnected"
                type="primary"
                size="small"
                :loading="youtrustActionLoading"
                :disabled="!authStore.userPermissions.is_org_admin"
                @click="showYoutrustModal = true"
              >
                Connect
              </gaia-button>
              <gaia-popconfirm
                v-if="isYoutrustConnected"
                title="Remove the Youtrust integration?"
                confirm-label="Remove"
                :width="320"
                @confirm="handleYoutrustRemove"
              >
                <template #trigger>
                  <gaia-button
                    size="small"
                    type="error"
                    tertiary
                    :loading="youtrustActionLoading"
                    :disabled="!authStore.userPermissions.is_org_admin"
                  >
                    Remove
                  </gaia-button>
                </template>
                Signature requests already out for signing keep running; new ones cannot be sent
                until Youtrust is connected again.
              </gaia-popconfirm>
            </template>
          </gaia-integration-card>

          <gaia-integration-card
            title="Slack"
            :tone="slackStateTone"
            :status-label="slackStateLabel"
            :message="slackHelperMessage"
          >
            <template v-if="isSlackConnected && slackStatus?.team_name" #meta>
              <gaia-text size="micro" tone="muted"
                >Workspace: {{ slackStatus.team_name }}</gaia-text
              >
            </template>

            <template #actions>
              <gaia-button
                v-if="!isSlackConnected"
                type="primary"
                size="small"
                :loading="slackActionLoading"
                :disabled="!authStore.userPermissions.is_org_admin"
                @click="handleSlackConnect"
              >
                Connect
              </gaia-button>
              <gaia-button v-if="isSlackConnected" size="small" @click="openSlackTestModal">
                Test Connection
              </gaia-button>
              <gaia-button
                v-if="isSlackConnected"
                size="small"
                :loading="slackActionLoading"
                :disabled="!authStore.userPermissions.is_org_admin"
                @click="handleSlackConnect"
              >
                Reconnect
              </gaia-button>
              <gaia-popconfirm
                v-if="isSlackConnected"
                title="Disconnect Slack?"
                confirm-label="Disconnect"
                :width="320"
                @confirm="handleSlackDisconnect"
              >
                <template #trigger>
                  <gaia-button
                    size="small"
                    type="error"
                    tertiary
                    :loading="slackActionLoading"
                    :disabled="!authStore.userPermissions.is_org_admin"
                  >
                    Disconnect
                  </gaia-button>
                </template>
                Playbooks with Slack notification actions will keep those actions, but they will
                fail on future runs until Slack is reconnected.
              </gaia-popconfirm>
            </template>
          </gaia-integration-card>

          <gaia-jira-card ref="jiraCard" />

          <gaia-integration-card :title="currentToken ? 'GAIA Token' : 'GAIA Integration'">
            <template v-if="currentToken">
              <n-flex vertical :size="0" :wrap="false">
                <gaia-field-row label="Name" variant="table">{{
                  currentToken.name
                }}</gaia-field-row>
                <gaia-field-row label="GAIA Token" variant="table">
                  {{ currentToken.token }}
                </gaia-field-row>
                <gaia-field-row label="Created" variant="table" :divider="false">
                  {{ new Date(currentToken.created_at).toLocaleString() }}
                </gaia-field-row>
              </n-flex>
            </template>
            <gaia-text v-else size="body" tone="text" :style="{ lineHeight: 1.5 }">
              Create one GAIA token for your organization to enable GAIA integration.
            </gaia-text>

            <template #actions>
              <template v-if="currentToken">
                <gaia-button
                  type="primary"
                  size="small"
                  :disabled="!authStore.userPermissions.is_org_admin"
                  @click="openEditModal"
                >
                  Edit GAIA Token
                </gaia-button>
                <gaia-popconfirm
                  title="Delete the GAIA token?"
                  confirm-label="Delete"
                  :width="320"
                  @confirm="deleteToken"
                >
                  <template #trigger>
                    <gaia-button
                      size="small"
                      type="error"
                      tertiary
                      :disabled="!authStore.userPermissions.is_org_admin"
                    >
                      Delete GAIA Token
                    </gaia-button>
                  </template>
                  Anything authenticating with this token stops working immediately.
                </gaia-popconfirm>
              </template>
              <gaia-button
                v-else
                type="primary"
                size="small"
                :disabled="!authStore.userPermissions.is_org_admin"
                @click="showCreateModal = true"
              >
                Create GAIA Token
              </gaia-button>
            </template>
          </gaia-integration-card>

          <gaia-integration-card
            title="Word Plugin"
            message="Install the GAIA add-in in Microsoft Word to draft and review contracts directly from your document."
          >
            <template #meta>
              <gaia-text size="micro" tone="muted"
                >Manifest: {{ wordPluginManifestFile }}</gaia-text
              >
            </template>

            <template #actions>
              <gaia-button type="primary" size="small" @click="handleDownloadWordPluginManifest">
                Download manifest
              </gaia-button>
              <gaia-button size="small" @click="wordPluginGuideModal?.show()">
                Installation guide
              </gaia-button>
            </template>
          </gaia-integration-card>
        </div>
      </gaia-spinner>

      <gaia-modal v-model:show="showCreateModal" title="Create New GAIA Token" width="500px">
        <TokenForm ref="tokenFormRef" :is-edit="false" @submit="handleCreateSubmit" />

        <template #footer>
          <n-flex justify="end" :size="8" :wrap="false">
            <gaia-button size="small" @click="showCreateModal = false">Cancel</gaia-button>
            <gaia-button
              type="primary"
              size="small"
              :loading="gaiaStore.tokenLoading"
              @click="tokenFormRef?.handleSubmit()"
            >
              Create GAIA Token
            </gaia-button>
          </n-flex>
        </template>
      </gaia-modal>

      <gaia-modal v-model:show="showEditModal" title="Edit GAIA Token" width="500px">
        <TokenForm
          ref="tokenFormRef"
          :initial-name="currentToken?.name || ''"
          :is-edit="true"
          @submit="handleEditSubmit"
        />

        <template #footer>
          <n-flex justify="end" :size="8" :wrap="false">
            <gaia-button size="small" @click="showEditModal = false">Cancel</gaia-button>
            <gaia-button
              type="primary"
              size="small"
              :loading="gaiaStore.tokenLoading"
              @click="tokenFormRef?.handleSubmit()"
            >
              Update GAIA Token
            </gaia-button>
          </n-flex>
        </template>
      </gaia-modal>

      <gaia-modal v-model:show="showYoutrustModal" title="Connect Youtrust" width="500px">
        <n-flex vertical :size="12" :wrap="false">
          <gaia-text size="body" tone="text" :style="{ lineHeight: 1.5 }">
            Add your Youtrust access token to enable signing integration for this organization.
          </gaia-text>
          <n-flex align="center" :size="8" :wrap="false">
            <gaia-switch v-model:value="youtrustIntegrationSandboxMode" size="small">
              <template #checked> Sandbox </template>
              <template #unchecked> Normal </template>
            </gaia-switch>

            <gaia-tooltip>
              <template #trigger>
                <!-- The help mark is a glyph, not copy: drawn here in the house
                     hand rather than pulled from an icon pack. -->
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="8" cy="8" r="6" />
                  <path d="M6.4 6.2a1.7 1.7 0 1 1 1.9 1.9v1" />
                  <path d="M8.3 11.2h.01" />
                </svg>
              </template>
              If you enable Sandbox Mode, you can test the integrations. You then need to supply 2
              API keys: One for signing, which should be a Sandbox key. One for Management (which is
              only used to set up webhooks)
            </gaia-tooltip>
          </n-flex>
          <div v-if="!youtrustIntegrationSandboxMode">
            <gaia-password-input
              v-model:value="youtrustSigningApiKey"
              placeholder="Enter Youtrust API Key"
            />
          </div>
          <div v-else>
            <gaia-input-group>
              <gaia-input-group-label style="min-width: 230px">
                Signing API Key (Sandbox)
              </gaia-input-group-label>
              <gaia-password-input
                v-model:value="youtrustSigningApiKey"
                placeholder="Enter Youtrust Signing API Key"
              />
            </gaia-input-group>
            <gaia-input-group>
              <gaia-input-group-label style="min-width: 230px">
                Management API Key (Normal)
              </gaia-input-group-label>
              <gaia-password-input
                v-model:value="youtrustManagementApiKey"
                placeholder="Enter Youtrust Management API Key"
              />
            </gaia-input-group>
          </div>
        </n-flex>

        <template #footer>
          <n-flex justify="end" :size="8" :wrap="false">
            <gaia-button size="small" @click="resetYoutrustModal">Cancel</gaia-button>
            <gaia-button
              type="primary"
              size="small"
              :loading="youtrustActionLoading"
              :disabled="!authStore.userPermissions.is_org_admin || !youtrustApiKeyFormValid"
              @click="handleYoutrustConnect"
            >
              Connect
            </gaia-button>
          </n-flex>
        </template>
      </gaia-modal>

      <gaia-modal v-model:show="showSlackTestModal" title="Test Slack connection">
        <n-flex vertical :size="12" :wrap="false">
          <gaia-text size="body" tone="text" :style="{ lineHeight: 1.5 }">
            Sends a message to the channel you pick, so you can confirm Gaia can post to this
            workspace.
          </gaia-text>
          <gaia-remote-select
            v-model:value="slackTestChannelId"
            clearable
            placeholder="Select a channel to test"
            :debounce="400"
            :fetch="fetchSlackChannelOptions"
            @error="onSlackChannelError"
          />
        </n-flex>
        <template #footer>
          <n-flex justify="end" :size="8" :wrap="false">
            <gaia-button size="small" @click="showSlackTestModal = false">Cancel</gaia-button>
            <gaia-button
              type="primary"
              size="small"
              :loading="slackTestSending"
              :disabled="!slackTestChannelId"
              @click="handleSlackSendTestMessage"
            >
              Send test message
            </gaia-button>
          </n-flex>
        </template>
      </gaia-modal>

      <GoogleDriveVerificationNoticeModal
        ref="googleDriveNoticeModal"
        @confirm="handleGoogleConnect"
      />

      <WordPluginInstallationGuideModal
        ref="wordPluginGuideModal"
        @download="handleDownloadWordPluginManifest"
      />
    </n-flex>
  </Layout>
</template>

<style scoped>
/* The card grid: one column per provider, reflowing as the window narrows.
   GaiaIntegrationCard owns the equal-height behaviour this stretch feeds. */
.integrations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
  align-items: stretch;
}
</style>
